import { useEffect, useState } from "react";
import { walletService } from "../../services/walletService";
import useToken from "../../../../hooks/auth/useToken";
import { Wallet, WalletHistory, WalletStats } from "../../types/Wallet";

interface Props {}

interface PaginationInfo {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export default function useWallets({}: Props) {
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [histories, setHistories] = useState<WalletHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<any>();
  const [walletsPagination, setWalletsPagination] = useState<PaginationInfo | null>(null);
  const [historiesPagination, setHistoriesPagination] = useState<PaginationInfo | null>(null);
  const [stats, setStats] = useState<WalletStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const { token } = useToken();

  const getWallets = async (
    page: number = 1,
    pageSize: number = 10,
    userId?: string,
    sortBy: string = "name",
    sortOrder: string = "asc",
    negativeOnly: boolean = false
  ) => {
    setLoading(true);
    try {
      const data = await walletService.getWallets(token, page, pageSize, userId, sortBy, sortOrder, negativeOnly);
      setWallets(data.wallets);
      setWalletsPagination(data.pagination);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setError(error);
      setLoading(false);
    }
  };

  const getHistories = async (
    page: number = 1,
    pageSize: number = 50,
    userId?: string,
    walletId?: number,
    type?: string,
    sortBy: string = "created_at",
    sortOrder: string = "desc",
    startDate?: string,
    endDate?: string,
    status?: string
  ) => {
    setHistoryLoading(true);
    try {
      const data = await walletService.getWalletHistories(
        token,
        page,
        pageSize,
        userId,
        walletId,
        type,
        sortBy,
        sortOrder,
        startDate,
        endDate,
        status
      );
      setHistories(data.histories);
      setHistoriesPagination(data.pagination);
      setHistoryLoading(false);
    } catch (error) {
      console.error(error);
      setError(error);
      setHistoryLoading(false);
    }
  };

  const searchHistories = async (
    searchQuery: string,
    page: number = 1,
    pageSize: number = 50,
    type?: string,
    sortBy: string = "created_at",
    sortOrder: string = "desc",
    startDate?: string,
    endDate?: string,
    status?: string
  ) => {
    setHistoryLoading(true);
    try {
      const data = await walletService.searchWalletHistories(
        token,
        searchQuery,
        page,
        pageSize,
        type,
        sortBy,
        sortOrder,
        startDate,
        endDate,
        status
      );
      setHistories(data.histories);
      setHistoriesPagination(data.pagination);
      setHistoryLoading(false);
    } catch (error) {
      console.error(error);
      setError(error);
      setHistoryLoading(false);
    }
  };

  const searchWallets = async (
    searchQuery: string,
    page: number = 1,
    pageSize: number = 10,
    sortBy: string = "name",
    sortOrder: string = "asc"
  ) => {
    setLoading(true);
    try {
      const data = await walletService.searchWallets(token, searchQuery, page, pageSize, sortBy, sortOrder);
      setWallets(data.wallets);
      setWalletsPagination(data.pagination);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setError(error);
      setLoading(false);
    }
  };

  const getStats = async () => {
    setStatsLoading(true);
    try {
      const data = await walletService.getWalletStats(token);
      setStats(data);
      setStatsLoading(false);
    } catch (error) {
      console.error(error);
      setError(error);
      setStatsLoading(false);
    }
  };

  const rechargeWallet = async (data: {
    user_id: string;
    country_id: number;
    amount: number;
    description?: string;
  }) => {
    try {
      const result = await walletService.adminRechargeWallet(token, data);
      getWallets();
      getHistories();
      getStats();
      return result;
    } catch (error) {
      throw error;
    }
  };

  const debitWallet = async (data: { user_id: string; country_id: number; amount: number; description?: string }) => {
    try {
      await walletService.debitWallet(token, data);
      getWallets();
      getHistories();
      getStats();
    } catch (error) {
      throw error;
    }
  };

  const updateTransactionStatus = async (transactionId: string, wallet_id: string, status: WalletHistory["status"]) => {
    try {
      await walletService.updateStatus(token, {
        id: transactionId,
        wallet_id,
        status,
      });
      getStats();
    } catch (error) {
      throw error;
    }
  };

  const updateDailyLimit = async (walletId: string, dailyLimit: number) => {
    await walletService.updateDailyLimit(token, walletId, dailyLimit);
    getWallets();
  };

  useEffect(() => {
    if (token) getWallets();
  }, [token]);

  return {
    wallets,
    histories,
    walletsLoading: loading,
    historyLoading,
    walletsError: error,
    walletsPagination,
    historiesPagination,
    stats,
    statsLoading,
    getWallets,
    getHistories,
    searchWallets,
    searchHistories,
    getStats,
    rechargeWallet,
    debitWallet,
    updateTransactionStatus,
    updateDailyLimit,
    length: wallets.length,
  };
}
